/**
 * Change Order API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  ChangeOrder,
  ChangeOrderListParams,
  ChangeOrderListResponse,
  ChangeOrderLog,
  CreateChangeOrderRequest,
  UpdateChangeOrderRequest,
  ApproveInternalRequest,
  RejectInternalRequest,
  SendToClientRequest,
  ClientApproveRequest,
  ClientRejectRequest,
  ClientNegotiateRequest,
  VoidChangeOrderRequest,
  ClientChangeOrder,
} from '../types/changeOrder';

/**
 * Change Order API Endpoints
 */
export const changeOrderApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // CHANGE ORDER CRUD
    // ============================================================================

    // Get all change orders for a project
    getChangeOrders: builder.query<
      ApiResponse<ChangeOrderListResponse>,
      { projectId: string; params?: ChangeOrderListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/change-orders`,
        params,
      }),
      providesTags: ['ChangeOrder'],
    }),

    // Get single change order
    getChangeOrder: builder.query<
      ApiResponse<ChangeOrder>,
      { projectId: string; changeOrderId: string }
    >({
      query: ({ projectId, changeOrderId }) => ({
        url: `/projects/${projectId}/change-orders/${changeOrderId}`,
      }),
      providesTags: (_result, _error, { changeOrderId }) => [
        { type: 'ChangeOrder', id: changeOrderId },
      ],
    }),

    // Create change order
    createChangeOrder: builder.mutation<
      ApiResponse<ChangeOrder>,
      { projectId: string; data: CreateChangeOrderRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/change-orders`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ChangeOrder'],
    }),

    // Update change order
    updateChangeOrder: builder.mutation<
      ApiResponse<ChangeOrder>,
      { projectId: string; changeOrderId: string; data: UpdateChangeOrderRequest }
    >({
      query: ({ projectId, changeOrderId, data }) => ({
        url: `/projects/${projectId}/change-orders/${changeOrderId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { changeOrderId }) => [
        'ChangeOrder',
        { type: 'ChangeOrder', id: changeOrderId },
      ],
    }),

    // Delete change order
    deleteChangeOrder: builder.mutation<
      ApiResponse<void>,
      { projectId: string; changeOrderId: string }
    >({
      query: ({ projectId, changeOrderId }) => ({
        url: `/projects/${projectId}/change-orders/${changeOrderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ChangeOrder'],
    }),

    // ============================================================================
    // INTERNAL APPROVAL WORKFLOW
    // ============================================================================

    // Submit for internal approval
    submitForInternalApproval: builder.mutation<
      ApiResponse<ChangeOrder>,
      { projectId: string; changeOrderId: string }
    >({
      query: ({ projectId, changeOrderId }) => ({
        url: `/projects/${projectId}/change-orders/${changeOrderId}/submit-internal`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { changeOrderId }) => [
        'ChangeOrder',
        { type: 'ChangeOrder', id: changeOrderId },
      ],
    }),

    // Approve internally
    approveInternal: builder.mutation<
      ApiResponse<ChangeOrder>,
      { projectId: string; changeOrderId: string; data?: ApproveInternalRequest }
    >({
      query: ({ projectId, changeOrderId, data }) => ({
        url: `/projects/${projectId}/change-orders/${changeOrderId}/approve-internal`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { changeOrderId }) => [
        'ChangeOrder',
        { type: 'ChangeOrder', id: changeOrderId },
      ],
    }),

    // Reject internally
    rejectInternal: builder.mutation<
      ApiResponse<ChangeOrder>,
      { projectId: string; changeOrderId: string; data: RejectInternalRequest }
    >({
      query: ({ projectId, changeOrderId, data }) => ({
        url: `/projects/${projectId}/change-orders/${changeOrderId}/reject-internal`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { changeOrderId }) => [
        'ChangeOrder',
        { type: 'ChangeOrder', id: changeOrderId },
      ],
    }),

    // ============================================================================
    // CLIENT WORKFLOW
    // ============================================================================

    // Send to client
    sendToClient: builder.mutation<
      ApiResponse<ChangeOrder>,
      { projectId: string; changeOrderId: string; data: SendToClientRequest }
    >({
      query: ({ projectId, changeOrderId, data }) => ({
        url: `/projects/${projectId}/change-orders/${changeOrderId}/send-client`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { changeOrderId }) => [
        'ChangeOrder',
        { type: 'ChangeOrder', id: changeOrderId },
      ],
    }),

    // ============================================================================
    // CLIENT PORTAL (PUBLIC ENDPOINTS)
    // ============================================================================

    // Get change order for client portal
    getClientChangeOrder: builder.query<ApiResponse<ClientChangeOrder>, { accessToken: string }>({
      query: ({ accessToken }) => ({
        url: `/change-orders/client/${accessToken}`,
      }),
    }),

    // Client approve
    clientApprove: builder.mutation<
      ApiResponse<ChangeOrder>,
      { accessToken: string; data: ClientApproveRequest }
    >({
      query: ({ accessToken, data }) => ({
        url: `/change-orders/client/${accessToken}/approve`,
        method: 'POST',
        body: data,
      }),
    }),

    // Client reject
    clientReject: builder.mutation<
      ApiResponse<ChangeOrder>,
      { accessToken: string; data: ClientRejectRequest }
    >({
      query: ({ accessToken, data }) => ({
        url: `/change-orders/client/${accessToken}/reject`,
        method: 'POST',
        body: data,
      }),
    }),

    // Client negotiate
    clientNegotiate: builder.mutation<
      ApiResponse<ChangeOrder>,
      { accessToken: string; data: ClientNegotiateRequest }
    >({
      query: ({ accessToken, data }) => ({
        url: `/change-orders/client/${accessToken}/negotiate`,
        method: 'POST',
        body: data,
      }),
    }),

    // ============================================================================
    // VOID
    // ============================================================================

    // Void change order
    voidChangeOrder: builder.mutation<
      ApiResponse<ChangeOrder>,
      { projectId: string; changeOrderId: string; data?: VoidChangeOrderRequest }
    >({
      query: ({ projectId, changeOrderId, data }) => ({
        url: `/projects/${projectId}/change-orders/${changeOrderId}/void`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { changeOrderId }) => [
        'ChangeOrder',
        { type: 'ChangeOrder', id: changeOrderId },
      ],
    }),

    // ============================================================================
    // REPORTS
    // ============================================================================

    // Get change order log
    getChangeOrderLog: builder.query<ApiResponse<ChangeOrderLog>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/change-orders/log`,
      }),
      providesTags: ['ChangeOrder'],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // CRUD
  useGetChangeOrdersQuery,
  useGetChangeOrderQuery,
  useCreateChangeOrderMutation,
  useUpdateChangeOrderMutation,
  useDeleteChangeOrderMutation,
  // Internal Approval Workflow
  useSubmitForInternalApprovalMutation,
  useApproveInternalMutation,
  useRejectInternalMutation,
  // Client Workflow
  useSendToClientMutation,
  // Client Portal
  useGetClientChangeOrderQuery,
  useClientApproveMutation,
  useClientRejectMutation,
  useClientNegotiateMutation,
  // Void
  useVoidChangeOrderMutation,
  // Reports
  useGetChangeOrderLogQuery,
} = changeOrderApi;

/**
 * Utility functions for PDF operations
 */
export const getChangeOrderPdfUrl = (projectId: string, changeOrderId: string): string => {
  return `/api/projects/${projectId}/change-orders/${changeOrderId}/pdf`;
};

export const getChangeOrderPdfPreviewUrl = (projectId: string, changeOrderId: string): string => {
  return `/api/projects/${projectId}/change-orders/${changeOrderId}/pdf/preview`;
};

export const downloadChangeOrderPdf = async (
  projectId: string,
  changeOrderId: string,
  coNumber: string
): Promise<void> => {
  const url = getChangeOrderPdfUrl(projectId, changeOrderId);
  const token = localStorage.getItem('auth_token');

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download PDF');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `CO-${coNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Re-export types for convenience
 */
export type { ChangeOrderListParams } from '../types/changeOrder';
